import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CollectionsIcon from '@mui/icons-material/Collections';
import MovieIcon from '@mui/icons-material/Movie';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import ProfileSectionCard from './ProfileSectionCard';
import StandardImageList from './ImageList';
import ProfileVideoList from './ProfileVideoList';
import VideoUploadButton from './VideoUploadButton';
import UploadButton from '../../components/gallery/UploadButton';

const steps = [
  { label: 'Údaje', title: 'Základné údaje', icon: <PersonIcon /> },
  { label: 'Fotografie', title: 'Fotografie', icon: <CollectionsIcon /> },
  { label: 'Videá', title: 'Videá', icon: <MovieIcon /> },
  { label: 'Kontrola', title: 'Kontrola a uloženie', icon: <CheckCircleOutlineIcon /> },
];

function formatReadableDate(value) {
  if (!value) return 'Nie je vyplnené';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Nie je vyplnené';
  return date.toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function SummaryRow({ label, value }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '220px minmax(0, 1fr)' },
        gap: { xs: 0.5, sm: 3 },
        alignItems: 'center',
        py: 1.5,
        borderBottom: '1px solid rgba(44, 62, 80, 0.08)',
      }}
    >
      <Typography sx={{ color: 'text.secondary', fontWeight: 700, textAlign: { xs: 'center', sm: 'right' } }}>{label}</Typography>
      <Typography sx={{ color: '#182b3a', fontWeight: 800, textAlign: { xs: 'center', sm: 'left' } }}>{value}</Typography>
    </Box>
  );
}

export default function ProfileEditorWizard({
  profileId,
  profileData,
  gallery,
  videos,
  setGallery,
  setVideos,
  handleInputChange,
  onSave,
  onCancel,
  cb,
}) {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    setActiveStep(0);
  }, [profileId]);

  const photoCount = gallery?.gallery?.length || 0;
  const videoCount = videos?.videos?.length || 0;
  const isLastStep = activeStep === steps.length - 1;

  const canContinue = useMemo(() => {
    if (activeStep !== 0) return true;
    return Boolean(profileData.name && profileData.birthDate && profileData.deathDate);
  }, [activeStep, profileData]);

  const goNext = () => {
    if (!isLastStep) {
      setActiveStep((value) => value + 1);
    }
  };

  const goBack = () => {
    setActiveStep((value) => Math.max(0, value - 1));
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1240, mx: 'auto' }}>
      <ProfileSectionCard
        title="Upraviť profil"
        subtitle="Prejdite jednotlivé kroky postupne. Veľké modré tlačidlo vás vždy posunie ďalej. Zmeny sa uložia až na poslednom kroku."
        contentSx={{ pt: 1 }}
      >
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: { xs: 3, md: 4 },
            maxWidth: 860,
            mx: 'auto',
            '& .MuiStepLabel-label': { fontSize: { xs: '0.85rem', sm: '1rem' }, fontWeight: 700 },
            '& .MuiStepIcon-root.Mui-active': { color: '#4866c9' },
            '& .MuiStepIcon-root.Mui-completed': { color: '#5d8f6b' },
          }}
        >
          {steps.map((step) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 420 }}>
          {activeStep === 0 && (
            <ProfileSectionCard
              title="1. Základné údaje"
              subtitle="Vyplňte meno, dátumy a krátky text spomienky. Tieto údaje sú zobrazené úplne hore na profile."
              sx={{ boxShadow: 'none', background: '#fff' }}
            >
              <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <TextField
                    label="Meno zosnulého"
                    name="name"
                    value={profileData.name}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    helperText="Napríklad: Ján Novák"
                    inputProps={{ style: { fontSize: '1.1rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Dátum narodenia"
                    name="birthDate"
                    type="date"
                    value={profileData.birthDate}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { fontSize: '1.1rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Dátum úmrtia"
                    name="deathDate"
                    type="date"
                    value={profileData.deathDate}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { fontSize: '1.1rem' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Krátka spomienka alebo životopis"
                    name="biography"
                    value={profileData.biography}
                    onChange={handleInputChange}
                    multiline
                    rows={6}
                    fullWidth
                    helperText="Stačí pár viet. Text sa dá kedykoľvek upraviť."
                    inputProps={{ style: { fontSize: '1.05rem', lineHeight: 1.65 } }}
                  />
                </Grid>
                </Grid>
              </Box>
              {!canContinue && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  Pred pokračovaním vyplňte meno a oba dátumy.
                </Alert>
              )}
            </ProfileSectionCard>
          )}

          {activeStep === 1 && (
            <ProfileSectionCard
              title="2. Fotografie"
              subtitle="Fotografie sa budú zobrazovať v peknej prezentácii na profile. Môžete ich pridať viac naraz."
              action={<Chip label={`${photoCount} ${photoCount === 1 ? 'fotografia' : photoCount > 1 && photoCount < 5 ? 'fotografie' : 'fotografií'}`} />}
              sx={{ boxShadow: 'none', background: '#fff' }}
            >
              <Alert severity="info" sx={{ mb: 3 }}>
                Odporúčanie: nahrajte jasné fotografie vo formáte JPG alebo PNG. Poradie fotografií sa zobrazí podľa poradia nahratia.
              </Alert>
              {photoCount > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <StandardImageList edit itemData={gallery.gallery} setGallery={setGallery} />
                </Box>
              ) : (
                <Paper
                  elevation={0}
                  sx={{ p: 4, mb: 3, textAlign: 'center', border: '1px dashed rgba(44, 62, 80, 0.25)', borderRadius: 3 }}
                >
                  <CollectionsIcon sx={{ fontSize: 54, color: '#709db3', mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Zatiaľ nie sú pridané žiadne fotografie</Typography>
                  <Typography color="text.secondary">Kliknite na tlačidlo nižšie a vyberte fotografie z počítača alebo telefónu.</Typography>
                </Paper>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <UploadButton
                  name="gallery"
                  gallery={gallery}
                  setGallery={setGallery}
                  cb={cb}
                  multiple
                  path="gallery"
                  left={false}
                  label="Pridať fotografie"
                  accept="image/*"
                />
              </Box>
            </ProfileSectionCard>
          )}

          {activeStep === 2 && (
            <ProfileSectionCard
              title="3. Videá"
              subtitle="Pridajte krátke spomienkové videá. Väčšie videá sa po nahratí automaticky zmenšia na vhodnú veľkosť."
              action={<Chip label={`${videoCount} ${videoCount === 1 ? 'video' : videoCount > 1 && videoCount < 5 ? 'videá' : 'videí'}`} />}
              sx={{ boxShadow: 'none', background: '#fff' }}
            >
              <Alert severity="info" sx={{ mb: 3 }}>
                Aktuálny limit: maximálne 3 videá na profil, každé do 3 minút. Video sa po nahratí optimalizuje, preto môže spracovanie chvíľu trvať.
              </Alert>
              <ProfileVideoList edit itemData={videos.videos} setVideos={setVideos} />
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <VideoUploadButton profileId={profileId} videos={videos.videos} setVideos={setVideos} />
              </Box>
            </ProfileSectionCard>
          )}

          {activeStep === 3 && (
            <ProfileSectionCard
              title="4. Kontrola a uloženie"
              subtitle="Skontrolujte údaje. Ak je všetko v poriadku, stlačte Uložiť zmeny."
              sx={{ boxShadow: 'none', background: '#fff' }}
            >
              <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, sm: 4 },
                    border: '1px solid rgba(44, 62, 80, 0.1)',
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.92)',
                  }}
                >
                  <SummaryRow label="Meno" value={profileData.name || 'Nie je vyplnené'} />
                  <SummaryRow label="Dátum narodenia" value={formatReadableDate(profileData.birthDate)} />
                  <SummaryRow label="Dátum úmrtia" value={formatReadableDate(profileData.deathDate)} />
                  <SummaryRow label="Fotografie" value={`${photoCount} ks`} />
                  <SummaryRow label="Videá" value={`${videoCount} ks`} />
                  <Box sx={{ pt: 2.5 }}>
                    <Typography sx={{ color: 'text.secondary', fontWeight: 800, mb: 1.5, textAlign: 'center' }}>
                      Spomienkový text
                    </Typography>
                    <Typography
                      sx={{
                        maxWidth: 760,
                        mx: 'auto',
                        whiteSpace: 'pre-line',
                        lineHeight: 1.85,
                        color: '#40596a',
                        fontWeight: 500,
                        textAlign: 'left',
                      }}
                    >
                      {profileData.biography || 'Nie je vyplnený'}
                    </Typography>
                  </Box>
                </Paper>

                <Alert
                  severity="success"
                  icon={<CheckCircleOutlineIcon />}
                  sx={{
                    mt: 3,
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    borderRadius: 3,
                    '& .MuiAlert-message': { width: '100%' },
                  }}
                >
                  <Typography sx={{ fontWeight: 800, mb: 0.75 }}>Profil je pripravený na uloženie.</Typography>
                  <Typography variant="body2">
                    Po uložení sa zmeny zobrazia na verejnom profile. Fotografie a videá môžete neskôr znova upraviť.
                  </Typography>
                </Alert>
              </Box>
            </ProfileSectionCard>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            variant="text"
            color="inherit"
            onClick={onCancel}
            size="large"
            sx={{ minHeight: 48, fontWeight: 700 }}
          >
            Zrušiť úpravy
          </Button>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={goBack}
              disabled={activeStep === 0}
              startIcon={<ArrowBackIcon />}
              size="large"
              sx={{ minHeight: 48, fontWeight: 700 }}
            >
              Späť
            </Button>
            {isLastStep ? (
              <Button
                variant="contained"
                onClick={onSave}
                startIcon={<SaveIcon />}
                size="large"
                sx={{ minHeight: 52, px: 4, fontWeight: 800, color: 'white', background: '#4866c9' }}
              >
                Uložiť zmeny
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={goNext}
                disabled={!canContinue}
                endIcon={<ArrowForwardIcon />}
                size="large"
                sx={{ minHeight: 52, px: 4, fontWeight: 800, color: 'white', background: '#4866c9' }}
              >
                Pokračovať
              </Button>
            )}
          </Stack>
        </Stack>
      </ProfileSectionCard>
    </Box>
  );
}
